import { useState } from 'react';

import { TFunction, useTranslation } from '@mint-lib/i18n';
import { usePermissions } from '@mint-lib/profile-helpers';
import {
  SuggestionDatePicker,
  SuggestionFilter,
  SuggestionPanel,
} from '@mint-lib/recommendations-circuit';

import { Stack, Tab, Tabs } from '@myn/mui';

import { RecommendationSettings } from '../../../features/Recommendations/index.jsx';
import { useCampaignDataSelector } from '../../../hooks/useGetCampaignData.js';
import {
  RecommendationsSettings,
  RecoSettingsGoals,
} from '../../../types/Recommendations.js';

enum MODAL_TABS {
  Recents = 'Recents',
  Settings = 'Settings',
}

enum TABS_ICONS {
  MagicWandFilled = 'MagicWandFilled',
  Settings = 'Settings',
}

const getMapTabsNameToLabel = (t: TFunction) => ({
  [MODAL_TABS.Recents]: t('Recents'),
  [MODAL_TABS.Settings]: t('Settings'),
});

const TABS = [
  {
    name: MODAL_TABS.Recents,
    icon: TABS_ICONS.MagicWandFilled,
  },
  {
    name: MODAL_TABS.Settings,
    icon: TABS_ICONS.Settings,
  },
];

type RecommendationsPanelProps = {
  recommendationsSettings: RecommendationsSettings;
  recoSettingsPriritisationGoal: RecoSettingsGoals | null;
  onChangeRecommendationsSettings: (
    settings: Partial<RecommendationsSettings>,
  ) => void;
  onCloseModal: () => void;
};

const RecommendationsPanel = ({
  recommendationsSettings,
  recoSettingsPriritisationGoal,
  onChangeRecommendationsSettings,
  onCloseModal,
}: RecommendationsPanelProps) => {
  const { t } = useTranslation('@myn/planning');

  const { data: journeyId } = useCampaignDataSelector('id');
  const [activeTabName, setActiveTabName] = useState<MODAL_TABS>(TABS[0].name);
  const { getPermissionField } = usePermissions();

  const isReadOnlyUserPermission =
    getPermissionField('notificationsFeature') === 'view';

  const tabs = TABS.map((tab) => {
    const tabLabels = getMapTabsNameToLabel(t);
    return (
      <Tab
        key={tab.name}
        icon={tab.icon}
        value={tab.name}
        label={tabLabels[tab.name]}
        aria-label={tab.name}
        onClick={() => setActiveTabName(tab.name)}
      />
    );
  });

  const tabContent = {
    [MODAL_TABS.Recents]: (
      <>
        <SuggestionFilter />
        <SuggestionPanel campaignId={journeyId} />
      </>
    ),
    [MODAL_TABS.Settings]: (
      <RecommendationSettings
        settings={recommendationsSettings}
        recoSettingsPriritisationGoal={recoSettingsPriritisationGoal}
        onChange={onChangeRecommendationsSettings}
        isReadOnlyUserPermission={isReadOnlyUserPermission}
        onCloseModal={onCloseModal}
      />
    ),
  };

  return (
    <Stack>
      <Stack
        flexGrow={1}
        direction="row"
        justifyContent="space-between"
        gap={1}
      >
        <Tabs value={activeTabName} sx={{ width: '100%' }}>
          {tabs}
        </Tabs>
        <SuggestionDatePicker />
      </Stack>
      {tabContent[activeTabName]}
    </Stack>
  );
};
export default RecommendationsPanel;
